// @ts-strict-ignore
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { Service } from 'rainbow-web-sdk/services/service';

export const MAIN_SVC = 'MainService';
export class MainService extends Service {

    // Observable fires when application is visible
    public appVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    // Observable fires when application is active
    public appActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public appActiveAutoAway: boolean = true; // Allows SDK to turn off automatic change to away after detectionInterval passes
    private appActiveDelay: number = 180;
    private appActiveTimeout: any = null;
    private appActiveListener: any = null;

    // Observable fires when application is connected (XMPP)
    public appConnected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // Observable fires when application has connectivity
    public appConnectivity: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private appConnectivityValue: boolean = false;
    private appConnectivityTimeout: any = null;

    private broadcastChannel: BroadcastChannel;
    public broadcastChannelSubject: Subject<any>;
    public globalPopupMessageSubject: Subject<any> = null;
    public globalSubPopupMessageSubject: Subject<any> = null;
    public isIEBrowser: boolean;
    public isSafari: boolean;
    public isFirefox: boolean;
    public isDesktop: boolean;
    public teamsAppPaired: boolean = false;
    public apiHeadersConfiguration = [];

    public iFrameAppConfig = {
        appId: '',
        referrer: ''
    };

    private logger: LoggerService = LoggerService.getInstance();

    public static getInstance() {
        return Service.create(MAIN_SVC, MainService);
    }

    public static build() {
        return new MainService();
    }

    private constructor() {
        super();

        // SDK STUFF
        const win = window as any;
        if (!win.moduleIds) win.moduleIds = [];
        if (!win.config) {
            win.config = {
                xmpp: { protocol: 'wss', currentServer: null, port: '443', pingInterval: '60000' },
                webservices: { protocol: 'https', currentServer: null, port: '443' }
            };
        }

        // BROWSER DETERMINATION STUFF
        const ua = navigator.userAgent.toLowerCase();
        this.isIEBrowser = (navigator.appName === "Netscape" && navigator.appVersion.indexOf("Trident") > -1);
        this.isSafari = ua.includes("safari") && !ua.includes("chrome");
        this.isFirefox = ua.includes("firefox");
        this.isDesktop = ua.indexOf("qtwebengine") > -1 || ua.indexOf('electron/') > -1;

        // VISIBILITY STUFF
        window.addEventListener('blur', () => {
            this.logger.debug('[MainService] Application visibility change : false');
            this.appVisible.next(false);
        });

        window.addEventListener("focus", () => {
            this.logger.debug('[MainService] Application visibility change : true');
            this.appVisible.next(true);
        });

        // ACTIVITY STUFF
        this.appActiveListener = () => {
            if (!this.appActive.value) { this.appActive.next(true); }
            if (this.appActiveTimeout) { clearTimeout(this.appActiveTimeout); this.appActiveTimeout = null; }

            // Since the mouse is moving, we turn off our event hooks for 2 second
            window.document.removeEventListener('mousemove', this.appActiveListener);
            window.document.removeEventListener('keypress', this.appActiveListener);
            setTimeout(() => { this.startTimer(); }, 2000);
        };

        // CONNECTIVITY STUFF
        this.appConnectivityValue = navigator.onLine;

        window.addEventListener('offline', () => {
            this.logger.debug('[MainService] Connectivity change : down');
            if (this.appConnectivityTimeout) { clearInterval(this.appConnectivityTimeout); this.appConnectivityTimeout = null; }
            if (this.appConnectivityValue) {
                this.appConnectivityValue = false;
                this.appConnectivityTimeout = setTimeout(() => { this.appConnectivity.next(false); }, 2500);
            }
        });
        window.addEventListener('online', () => {
            this.logger.debug('[MainService] Connectivity change : up');
            if (this.appConnectivityTimeout) { clearInterval(this.appConnectivityTimeout); this.appConnectivityTimeout = null; }
            if (!this.appConnectivityValue) {
                this.appConnectivityValue = true;
                this.appConnectivityTimeout = setTimeout(() => { this.appConnectivity.next(true); }, 2500);
            }
        });

        if (win.BroadcastChannel) {
            // BROADCASTCHANNEL STUFF
            this.broadcastChannelSubject = new Subject<any>();

            // Configure tab communication channel
            this.broadcastChannel = new BroadcastChannel('rainbowBroadcastChannel');
            this.broadcastChannel.onmessage = (event: any) => { this.broadcastChannelSubject.next(event); };

            // GLOBALPOPUP STUFF
            this.globalPopupMessageSubject = new Subject<any>();
            this.globalSubPopupMessageSubject = new Subject<any>();
        }

        /*
            OVERWRITE FETCH WITH CUSTOM IMPLEMENTATION TO ADD MORE INFO ON API CALLS AND ANSWERS
        */
        const { fetch: originalFetch } = window;

        window.fetch = async (...args) => {
            const [resource, httpConfig] = args;
            let isRainbowUrl: boolean = false;

            if (config?.webservices) {
                const resources = resource.toString().split("?");
                isRainbowUrl = (resources[0].indexOf(config.webservices.currentServer) !== -1);
            }

            if (httpConfig?.headers && isRainbowUrl) {
                const OS = (navigator.platform.toUpperCase().indexOf("MAC") >= 0) ? "osx" : "win";

                if (window.sdk?.key?.appID) {
                    //add x-rainbow-client-id for SDK app
                    httpConfig.headers["x-rainbow-client-id"] = window.sdk.key.appID;
                    httpConfig.headers["x-rainbow-client"] = "web_sdk_" + OS;
                    httpConfig.headers["x-rainbow-client-version"] = window["sdkversion"] ? window["sdkversion"] : "9.99.9";
                    httpConfig.headers['x-rainbow-correlation-id'] = ("web_sdk_" + crypto.randomUUID()).substring(0, 20);
                } else {
                    const prefix = (this.isDesktopApp() ? "desk_" : "web_");
                    httpConfig.headers["x-rainbow-client"] = prefix + OS;
                    httpConfig.headers["x-rainbow-client-version"] = version;
                    httpConfig.headers['x-rainbow-correlation-id'] = (prefix + crypto.randomUUID()).substring(0, 20);
                }

                if (this.iFrameAppConfig?.appId) {
                    httpConfig.headers["x-rainbow-3rd-appid"] = this.iFrameAppConfig.appId;
                    httpConfig.headers["x-rainbow-3rd-referrer"] = this.iFrameAppConfig.referrer;
                }

                if (httpConfig) {
                    if (!httpConfig.method) httpConfig.method = "GET";
                    const resources = resource.toString().split("?");
                    if (resources[1]) { resources[0] += "?URI_HIDDEN_PARAMS"; }
                    const correlationId = httpConfig.headers['x-rainbow-correlation-id'];
                    const client = httpConfig.headers["x-rainbow-client"];
                    const appVersion = version;
                    this.logger.debug(`SEND [HTTP] [${httpConfig.method}] ${resources[0]} ${client} ${appVersion} ${correlationId}`);
                }

                //check for additional headers
                if (this.apiHeadersConfiguration?.length) {
                    this.apiHeadersConfiguration.forEach((apiConfig) => {
                        if (resource.toString().indexOf(apiConfig.url) !== -1 && (apiConfig.method === "*" || apiConfig.method === httpConfig.method)) {
                            //add the headers to the request
                            apiConfig.headers.forEach((additionalHeader) => {
                                httpConfig.headers[additionalHeader.key] = additionalHeader.value;
                            });
                        }
                    });
                }
            }

            try {
                // request interceptor here
                const response = await originalFetch(resource, httpConfig);
                // response interceptor here
                if (httpConfig) {
                    if (!httpConfig.method) httpConfig.method = "GET";
                    const resources = resource.toString().split("?");
                    if (resources[1]) { resources[0] += "?URI_HIDDEN_PARAMS"; }
                    const requestId = (response.headers?.get("x-rainbow-request-id")) ? ` (x-rainbow-request-id:${response.headers.get("x-rainbow-request-id")})` : '';
                    const correlationId = (response.headers?.get("x-rainbow-correlation-id")) ? ` (x-rainbow-correlation-id:${response.headers.get("x-rainbow-correlation-id")})` : '';
                    const appVersion = (response.headers?.get("x-rainbow-client-version")) ? ` (x-rainbow-client-version:${response.headers.get("x-rainbow-client-version")})` : '';
                    this.logger.debug(`RECV [HTTP] [${httpConfig.method}] [${response.status}] ${resources[0]} ${requestId} ${correlationId} ${appVersion}`);

                    if (localStorage.getItem("fullDebugMode") === "true") {
                        try {
                            const clone = response.clone();
                            clone.json().then((data) => {
                                const headerData = httpConfig.body ? JSON.stringify(JSON.parse(httpConfig.body as string), null, 3) : '';
                                this.logger.debug(`RECV [HTTP] [${httpConfig.method}] [${response.status}] ${resources[0]} ${requestId} ${correlationId} ${appVersion} with body ${headerData} and response data: ${JSON.stringify(data, null, 3)}`);
                            });
                        }
                        catch (__error) {

                        }
                    }
                }

                return response;
            }
            catch (error) {
                if (httpConfig) {
                    if (!httpConfig.method) httpConfig.method = "GET";
                    const resources = resource.toString().split("?");
                    if (resources[1]) { resources[0] += "?URI_HIDDEN_PARAMS"; }
                    const correlationId = (httpConfig.headers?.['x-rainbow-correlation-id']) ? ` (x-rainbow-correlation-id:${httpConfig.headers['x-rainbow-correlation-id']})` : '';
                    this.logger.warn(`[HTTP] [${httpConfig.method}] ${resources[0]} ${correlationId} -- ${error}`);
                }
                else {
                    this.logger.debug(`[HTTP] ${error}`);
                }

                throw error;
            }
        };

    }

    public configure(centralizedService: any = null, sdkConfig: any = null): void {
        if (sdkConfig) {
            this.appActiveAutoAway = sdkConfig.autoAway;
            this.appActiveDelay = sdkConfig.autoAwayDelay;
            this.startTimer();
            return;
        }

        this.logger.info('[MainService] RAINBOW SDK STARTED');
        if (!centralizedService?.isDesktopApp()) { this.startTimer(); }
        else {
            centralizedService.idle.setDetectionInterval(this.appActiveDelay);
            centralizedService.idle.onStateChanged.addListener((state: string) => { this.appActive.next(state === 'active'); });
        }
    }

    private startTimer() {
        if (this.appActiveTimeout) { clearTimeout(this.appActiveTimeout); this.appActiveTimeout = null; }
        this.appActiveTimeout = setTimeout(() => { this.appActive.next(false); }, this.appActiveDelay * 1000);
        window.document.addEventListener('mousemove', this.appActiveListener);
        window.document.addEventListener('keypress', this.appActiveListener);
    }

    public isDesktopApp() {
        const ua = navigator.userAgent.toLowerCase();
        return ua.indexOf("qtwebengine") > -1 || ua.indexOf('electron/') > -1;
    }

    public broadcastChannelSubscribe(handler: any): Subscription {
        return this.broadcastChannelSubject.subscribe(handler);
    }

    public postBroadcastChannelMessage(message: string) {
        this.broadcastChannel.postMessage(message);
    }

    public showGlobalPopupMessage(popupMessageData: any) {
        this.globalPopupMessageSubject.next({ show: true, data: popupMessageData });
    }

    public hideGlobalPopupMessage() {
        this.globalPopupMessageSubject.next({ show: false });
    }

    public showSubGlobalPopupMessage(popupMessageData: any) {
        this.globalSubPopupMessageSubject.next({ show: true, data: popupMessageData });
    }

    public hideSubGlobalPopupMessage() {
        this.globalSubPopupMessageSubject.next({ show: false });
    }

    public inject<T = any>(serviceName: string): T | undefined {
        if (window.rainbow.services[serviceName] === undefined) {
            if (serviceName === 'CentralizedService') return undefined;
            this.logger.info(`[mainService] Failed to inject service "${serviceName}: service not found"`);
        }
        return window.rainbow.services[serviceName];
    }

}
